import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, ArrowRight, Loader2, Info, UploadCloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const SignalerProbleme = () => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    sujet: '',
    description: '',
    secteur: 'Infrastructure / Voirie',
    telephone: user?.phone || '',
    localisation: '',
    type_demande: 'signalement'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let fichier_joint = null;
      
      // Upload file first if present
      if (file) {
        const uploadData = new FormData();
        uploadData.append('file', file);
        const uploadRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, uploadData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fichier_joint = uploadRes.data.path;
      }

      // Submit demande
      const payload = { ...formData, fichier_joint };
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/demandes`, payload, { withCredentials: true });
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-bold">Signalement enregistré</span>
          <span className="text-sm">Référence : {res.data.reference}</span>
        </div>,
        { duration: 5000 }
      );
      navigate(`/recipisse/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur lors de l\'envoi du signalement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-[#0A192F] text-white p-8 relative overflow-hidden border-b-4 border-white">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Signaler un problème</h1>
              <p className="text-blue-100 text-sm font-medium">
                Infrastructure dégradée, panne d'eau/électricité, ou incident sur la voie publique.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6" data-testid="form-signalement">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-blue-800 text-sm mb-6">
              <Info className="w-5 h-5 shrink-0 text-blue-600" />
              <p>Votre signalement sera transmis aux services techniques compétents. Précisez au mieux la localisation pour faciliter l'intervention.</p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Type d'incident *</label>
                <select 
                  name="sujet" 
                  value={formData.sujet} 
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0A192F] focus:border-[#0A192F] outline-none transition-all bg-white"
                  data-testid="input-type-incident"
                >
                  <option value="">Sélectionnez la nature du problème...</option>
                  <option value="Route dégradée / Nid de poule">Route dégradée / Nid de poule</option>
                  <option value="Panne d'éclairage public">Panne d'éclairage public</option>
                  <option value="Fuite d'eau sur voie publique">Fuite d'eau sur voie publique</option>
                  <option value="Problème d'assainissement / Déchets">Problème d'assainissement / Déchets</option>
                  <option value="Équipement public endommagé">Équipement public endommagé</option>
                  <option value="Autre incident">Autre incident</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Localisation précise *</label>
                <input 
                  type="text" 
                  name="localisation"
                  value={formData.localisation}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Croisement Av. Laurent Kabila et Av. Lualaba, Kolwezi"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0A192F] focus:border-[#0A192F] outline-none transition-all"
                  data-testid="input-localisation-signalement"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Description *</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Détaillez le problème observé..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0A192F] focus:border-[#0A192F] outline-none transition-all resize-none"
                  data-testid="input-description-signalement"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Téléphone pour suivi (Optionnel)</label>
                <input 
                  type="tel" 
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  placeholder="Si les techniciens doivent vous contacter"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0A192F] focus:border-[#0A192F] outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Photo justificative (Optionnel)</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-300 border-dashed rounded-xl hover:border-blue-900 transition-colors cursor-pointer relative bg-slate-50">
                  <div className="space-y-2 text-center">
                    <UploadCloud className="mx-auto h-8 w-8 text-slate-400" />
                    <div className="flex text-sm text-slate-600 justify-center">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-900 hover:text-blue-800 focus-within:outline-none px-2 py-1">
                        <span>Sélectionner un fichier</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*" />
                      </label>
                    </div>
                    <p className="text-xs text-slate-500">PNG, JPG, JPEG jusqu'à 10MB</p>
                    {file && (
                      <p className="text-sm font-bold text-emerald-600 mt-2 bg-emerald-50 py-1 px-2 rounded inline-block">
                        Fichier sélectionné : {file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100 flex justify-end">
              <Button 
                type="button" 
                variant="ghost" 
                className="mr-3 text-slate-500 hover:text-slate-700"
                onClick={() => navigate('/citoyen')}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#0A192F] hover:bg-blue-900 text-white px-8 h-12 rounded-xl border-none"
                data-testid="submit-signalement-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Signaler <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignalerProbleme;
